"use client";

import { useEffect, useState } from "react";
import { getForumToken, getForumUser, startForumLogin, WRITES_ENABLED } from "./PublicAuth";
import { API_BASE, SIGNUP_URL, fmt } from "../_lib/api";
import { CaretDownIcon, CaretUpIcon } from "./icons";
import { useTurnstileToken } from "./useTurnstile";

export class ForumPublicError extends Error {
  status: number;
  code: string | null;
  constructor(status: number, code: string | null, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

// What to tell a signed-in agent the backend won't let participate, and why.
// The public site can only read; the agency toggle / subscription / ban
// rules live in the app.
export function participationHint(err: unknown): string | null {
  const code = (err as ForumPublicError)?.code;
  switch (code) {
    case "FORUM_DISABLED_BY_AGENCY":
      return "Your agency hasn't turned on the Community yet. The agency owner can switch it on in the InsuraCentral app under Settings, Account, Preferences.";
    case "FORUM_OPT_IN_REQUIRED":
      return "Turn on the Community in the InsuraCentral app under Settings, Account, Preferences, then try again.";
    case "FORUM_SUBSCRIPTION_REQUIRED":
      return "Posting and voting need an active InsuraCentral subscription or trial. Reading stays free.";
    case "FORUM_DEMO_READ_ONLY":
      return "Demo accounts can read the floor but can't post, reply or vote. Start a free trial to join in.";
    case "FORUM_BANNED":
      return "Your account is suspended from the Community.";
    case "FORUM_NOT_AVAILABLE":
      return "The Community isn't available on your account.";
    default:
      return null;
  }
}

export async function authedFetch(path: string, options: RequestInit = {}) {
  const token = getForumToken();
  const res = await fetch(`${API_BASE}/api/v1/forum${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const json = await res.json().catch(() => null);
  const data = json?.data?.data || json?.data || json;
  if (!res.ok) {
    throw new ForumPublicError(res.status, json?.error || data?.error || null, data?.message || json?.message || `Request failed (${res.status})`);
  }
  return data;
}

// Anonymous visitors who try to vote get a plain sign-in card, not an OAuth redirect.
export function JoinModal({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Sign in to vote"
    >
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="fp-serif text-[22px] font-medium leading-tight text-slate-900">Votes are for agents on the floor.</h3>
        <p className="mt-2 text-[13px] leading-5 text-slate-500">
          Votes decide what rises. Sign in with your InsuraCentral account to make your read on this thread count.
        </p>
        <div className="mt-5 flex flex-col gap-2">
          {WRITES_ENABLED && (
            <button
              onClick={() => startForumLogin()}
              className="h-10 w-full rounded-md bg-blue-600 text-[14px] font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
            >
              Sign in with InsuraCentral
            </button>
          )}
          <a
            href={SIGNUP_URL}
            className={
              WRITES_ENABLED
                ? "flex h-10 w-full items-center justify-center rounded-md border border-slate-300 text-[14px] font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:text-slate-900"
                : "flex h-10 w-full items-center justify-center rounded-md bg-blue-600 text-[14px] font-semibold text-white transition-colors hover:bg-blue-700"
            }
          >
            Not a member? Join free
          </a>
        </div>
        <button onClick={onClose} className="mt-4 text-[12px] text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline">
          Not now
        </button>
      </div>
    </div>
  );
}

// Thread-page vote column. Anonymous visitors see the ISR-cached score;
// signed-in agents hydrate their own vote and the live number.
export function PublicVoteRow({ postId, initialScore, orientation = "vertical" }: { postId: string; initialScore: number; orientation?: "vertical" | "horizontal" }) {
  const [score, setScore] = useState(initialScore);
  const [myVote, setMyVote] = useState<1 | -1 | null>(null);
  const [authed, setAuthed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!getForumToken()) return;
    setAuthed(true);
    authedFetch(`/me/state?postIds=${postId}`)
      .then((state) => {
        if (state?.votes?.[postId]) setMyVote(state.votes[postId]);
        const fresh = state?.posts?.find((p: any) => p.id === postId);
        if (fresh) setScore(fresh.score);
      })
      .catch(() => {});
  }, [postId]);

  const cast = async (dir: 1 | -1) => {
    if (!authed) {
      setShowJoin(true);
      return;
    }
    if (busy) return;
    setBusy(true);
    const prev = myVote ?? 0;
    const next = prev === dir ? 0 : dir;
    setMyVote((next || null) as 1 | -1 | null);
    setScore((s) => s - prev + next);
    try {
      const fresh = await authedFetch(`/votes`, {
        method: "PUT",
        body: JSON.stringify({ targetType: "post", targetId: postId, value: next }),
      });
      setScore(fresh.score);
      setMyVote(fresh.myVote);
    } catch (err) {
      setMyVote((prev || null) as 1 | -1 | null);
      setScore((s) => s + prev - next);
      // Never fail silently: say why the vote didn't count.
      setNotice(participationHint(err) || (err as Error).message || "That vote didn't go through.");
    } finally {
      setBusy(false);
    }
  };

  const btn = "fp-inline-flex flex h-9 w-10 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 sm:h-7 sm:w-8";
  return (
    <div className={`select-none items-center ${orientation === "vertical" ? "flex flex-col" : "inline-flex flex-row gap-0.5 rounded-md border border-slate-200 bg-white px-1"}`}>
      {showJoin && <JoinModal onClose={() => setShowJoin(false)} />}
      {notice && (
        <div className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] text-amber-900 shadow-lg" role="alert">
          <p>{notice}</p>
          <button onClick={() => setNotice(null)} className="mt-1.5 text-[12px] font-semibold underline underline-offset-2">
            Dismiss
          </button>
        </div>
      )}
      <button
        aria-label="Upvote"
        aria-pressed={myVote === 1}
        onClick={() => cast(1)}
        className={`${btn} ${myVote === 1 ? "bg-blue-50 text-blue-700" : "text-slate-400 hover:bg-slate-100 hover:text-slate-800"}`}
      >
        <CaretUpIcon />
      </button>
      <span
        className={`fp-mono min-w-[2rem] text-center text-[15px] font-semibold tabular-nums ${orientation === "vertical" ? "my-0.5" : ""} ${
          myVote === 1 ? "text-blue-700" : myVote === -1 ? "text-red-600" : "text-slate-900"
        }`}
      >
        {fmt(score)}
      </span>
      <button
        aria-label="Downvote"
        aria-pressed={myVote === -1}
        onClick={() => cast(-1)}
        className={`${btn} ${myVote === -1 ? "bg-red-50 text-red-600" : "text-slate-400 hover:bg-red-50 hover:text-red-600"}`}
      >
        <CaretDownIcon />
      </button>
    </div>
  );
}

// Comment box for signed-in agents; join/sign-in card otherwise.
// Turnstile is enforced server-side for this surface — token comes from the
// invisible widget loaded on demand.
export function PublicCommentBox({ postId, locked }: { postId: string; locked: boolean }) {
  const [user, setUser] = useState<ReturnType<typeof getForumUser>>(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const turnstile = useTurnstileToken(!!user && !locked, "forum-turnstile");

  useEffect(() => setUser(getForumUser()), []);

  if (locked) return null;

  if (!user) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-5 py-4 sm:flex sm:items-center sm:justify-between sm:gap-4">
        <div>
          <p className="text-[14px] font-semibold text-slate-900">Have a take on this one?</p>
          <p className="mt-0.5 text-[13px] text-slate-500">
            {WRITES_ENABLED
              ? "InsuraCentral agents reply with one click — no second account."
              : "InsuraCentral members reply with one click — the CRM built for life agents."}
          </p>
        </div>
        <div className="mt-3 flex shrink-0 gap-2 sm:mt-0">
          {WRITES_ENABLED && (
            <button
              onClick={() => startForumLogin()}
              className="h-9 rounded-md border border-slate-300 px-3.5 text-[13px] font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:text-slate-900"
            >
              Sign in
            </button>
          )}
          <a href={SIGNUP_URL} className="flex h-9 items-center rounded-md bg-blue-600 px-3.5 text-[13px] font-semibold text-white transition-colors hover:bg-blue-700">
            Join free
          </a>
        </div>
      </div>
    );
  }

  const submit = async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await authedFetch(`/posts/${postId}/comments`, {
        method: "POST",
        headers: turnstile.headers,
        body: JSON.stringify({ body: body.trim() }),
      });
      setBody("");
      setNotice("Posted. It shows here within a few minutes, and right away in the InsuraCentral app.");
      turnstile.reset();
    } catch (e: any) {
      setNotice(participationHint(e) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, 10_000))}
        placeholder={`Reply as ${user.name}…`}
        rows={3}
        className="w-full resize-y rounded-md border border-slate-200 p-3 text-[14px] leading-6 text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
      />
      <div id="forum-turnstile" />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="fp-mono text-[11px] text-slate-400">Markdown ok · no client info · no pitches</span>
        <button
          onClick={submit}
          disabled={busy || !body.trim()}
          className="h-9 rounded-md bg-blue-600 px-4 text-[13px] font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? "Posting…" : "Post reply"}
        </button>
      </div>
      {notice && <p className="mt-2 text-[13px] text-slate-600">{notice}</p>}
    </div>
  );
}
